import React, { startTransition, useEffect, useMemo, useRef, useState } from "react";
import excelImg2 from "../../Assets/images/excel-img.svg";
import truckIcon from "../../Assets/images/Truck.svg";
import { QUOTE_SUCCESS_LOGO_URL, preloadQuoteSuccessLogo } from "../../utils/quoteSuccessLogo";
import useMakeRequest from "../../utils/ApiHelper";
import { useNavigate } from "react-router-dom";
import { ApiConstants } from "../../constants/ApiConstants";
import { Modal } from "react-bootstrap";
import NewAddress, { type NewAddressWizardHandle } from "../../Modal/NewAddress";
import { toast } from "react-toastify";
import { ApiTypes, StorageConstant, VALIDATION_MESSAGE } from "../../constants/Constants";
import BrandedLoader from "../layout/BrandedLoader";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { socket } from "../../utils/Socket";
import ExcelSheet from "../../Modal/ExcelSheet";
import {
  getBulkExcelInvalidDataToastMessage,
  validateBulkQuoteExcelBuffer,
} from "../../utils/bulkExcelValidate";
import { getFromStorage } from "../../utils/Helper";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import "./manual-quote-wizard.scss";
import { formatAddressListRow } from "../myquotation/view-quotation/helpers";

const TEMPLATE_HREF = `${process.env.PUBLIC_URL || ""}/assets/docs/QuoteNBUY.xlsx`;
const BULK_WIZARD_STATE_KEY = "qnBulkWizardStateV1";

/** Redownload only when this bulk attempt already has progress (not across unrelated requests). */
function bulkSessionHasWizardProgress(saved: {
  step?: number;
  fileName?: string;
  excelUrl?: string;
  data?: { address_id?: string; shipping_id?: string };
} | null): boolean {
  if (!saved) return false;
  if (saved.step != null && saved.step > 1) return true;
  if (String(saved.fileName ?? "").trim()) return true;
  if (String(saved.excelUrl ?? "").trim()) return true;
  if (String(saved.data?.address_id ?? "").trim()) return true;
  if (String(saved.data?.shipping_id ?? "").trim()) return true;
  return false;
}

const parseWizardStepFromQuery = (raw: string | null): 1 | 2 | 3 | null => {
  if (raw === "1" || raw === "2" || raw === "3") return Number(raw) as 1 | 2 | 3;
  return null;
};

function xlsxPreviewUrlFromResponse(body: any): string {
  if (!body || typeof body !== "object") return "";
  const fp = body.file_path ?? body?.data?.file_path ?? body?.filePath;
  return typeof fp === "string" && fp.trim() ? fp.trim() : "";
}

/** Quote ID for success popup — API may nest under `data`, use string/BIGINT-safe values. */
function formatQuotationIdForSuccessPopup(raw: unknown): string {
  if (raw == null || raw === "") return "—";
  if (typeof raw === "bigint") return raw.toString();
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "null") return "—";
  return s;
}

/** Same nesting as manual quote `RequestQuote` success handler (`data.data` vs `data`). */
function readQuotationIdFromQuotesCreateSuccess(body: any): string {
  if (!body || typeof body !== "object") return "—";
  const candidates = [
    body?.data?.data?.quotation_id,
    body?.data?.data?.quotationId,
    body?.data?.quotation_id,
    body?.data?.quotationId,
    body?.quotation_id,
    body?.quotationId,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const s = formatQuotationIdForSuccessPopup(c);
    if (s !== "—") return s;
  }
  return "—";
}

function extractQuotationIdFromApiBody(body: any): string {
  if (!body || typeof body !== "object") return "—";

  const tryPick = (o: any): string | null => {
    if (!o || typeof o !== "object" || Array.isArray(o)) return null;
    const v = o.quotation_id ?? o.quotationId;
    if (v != null && String(v).trim() !== "" && String(v).trim().toLowerCase() !== "null") {
      return formatQuotationIdForSuccessPopup(v);
    }
    return null;
  };

  const ordered = [
    body,
    body.data,
    body?.data?.data,
    body?.result,
    body?.payload,
  ];
  for (const o of ordered) {
    const s = tryPick(o);
    if (s && s !== "—") return s;
  }

  const walk = (o: any, depth: number): string | null => {
    if (depth > 8 || !o || typeof o !== "object") return null;
    const direct = tryPick(o);
    if (direct && direct !== "—") return direct;
    for (const v of Object.values(o)) {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const w = walk(v, depth + 1);
        if (w && w !== "—") return w;
      }
    }
    return null;
  };
  const found = walk(body, 0);
  return found && found !== "—" ? found : "—";
}

function ExcelSelect() {
  const addressList = useMakeRequest();
  const xlsxPreview = useMakeRequest();
  const shippingList = useMakeRequest();
  const Add = useMakeRequest();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const stepQuery = parseWizardStepFromQuery(searchParams.get("step"));
  const [addressModalKey, setAddressModalKey] = useState(0);
  const [firstAddressFormKey, setFirstAddressFormKey] = useState(0);
  const firstAddressWizardRef = useRef<NewAddressWizardHandle | null>(null);
  const [showInlineNewAddressForm, setShowInlineNewAddressForm] = useState(false);
  const [data, setData] = useState<{ address_id?: string; shipping_id?: string }>({});
  const [error, setError] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | "">("");
  const [fileName, setFileName] = useState("");
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [previewApiLoading, setPreviewApiLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** Public file URL from `quotes-xlsx-preview` for Office Online embed (multi-tab preview). */
  const [excelUrl, setExcelUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [shippingMeta, setShippingMeta] = useState({
    delivery_instructions: "",
  });
  const [templateDownloaded, setTemplateDownloaded] = useState(false);

  useEffect(() => {
    preloadQuoteSuccessLogo();
  }, []);

  /** Hydrate bulk wizard state after reload (e.g. language switch) for the same in-progress request only. */
  useEffect(() => {
    let saved: any = null;
    try {
      const raw = window.sessionStorage.getItem(BULK_WIZARD_STATE_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    try {
      window.localStorage.removeItem("qnBulkExcelTemplateDownloaded");
    } catch {
      /* legacy flag — no longer used */
    }
    const hasProgress = bulkSessionHasWizardProgress(saved);
    if (!hasProgress) {
      try {
        window.sessionStorage.removeItem(BULK_WIZARD_STATE_KEY);
      } catch {
        /* ignore */
      }
      setTemplateDownloaded(false);
      return;
    }
    const nextStep = stepQuery ?? (saved?.step === 1 || saved?.step === 2 || saved?.step === 3 ? saved.step : 1);
    if (nextStep !== step) setStep(nextStep);
    if (saved?.data && typeof saved.data === "object") {
      setData((prev) => ({ ...prev, ...saved.data }));
    }
    if (saved?.shippingMeta && typeof saved.shippingMeta === "object") {
      setShippingMeta((prev) => ({ ...prev, ...saved.shippingMeta }));
    }
    if (typeof saved?.fileName === "string") setFileName(saved.fileName);
    if (typeof saved?.excelUrl === "string") setExcelUrl(saved.excelUrl);
    if (typeof saved?.templateDownloaded === "boolean") {
      setTemplateDownloaded(saved.templateDownloaded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Keep current bulk step in URL so language reload stays on same tab. */
  useEffect(() => {
    const currentStepInUrl = searchParams.get("step");
    const nextStep = String(step);
    if (currentStepInUrl === nextStep) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("step", nextStep);
    setSearchParams(nextParams, { replace: true });
  }, [step, searchParams, setSearchParams]);

  /** Persist bulk wizard state across reloads (language switch). */
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        BULK_WIZARD_STATE_KEY,
        JSON.stringify({
          step,
          data,
          shippingMeta,
          fileName,
          excelUrl,
          templateDownloaded,
        }),
      );
    } catch {
      /* ignore storage quota / privacy mode */
    }
  }, [step, data, shippingMeta, fileName, excelUrl, templateDownloaded]);

  /* Prefetch address + shipping so moving to Shipping does not wait on first paint. */
  useEffect(() => {
    addressList.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
    shippingList.makeRequest({ url: ApiConstants?.ShippingList, toastStatus: false });
  }, []);

  useEffect(() => {
    if (step !== 2) setShowInlineNewAddressForm(false);
  }, [step]);

  /** Pre-select primary delivery address on shipping tab when none is chosen yet. */
  useEffect(() => {
    if (step !== 2) return;
    if (String(data.address_id ?? "").trim() !== "") return;

    const rows = addressList?.data?.data?.rows;
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) return;

    const primary =
      list.find(
        (r: any) => Number(r?.is_primary) === 1 || String(r?.is_primary) === "1",
      ) ?? list[0];

    if (primary?.id == null) return;

    setData((s) => ({
      ...s,
      address_id: String(primary.id),
    }));
  }, [step, data.address_id, addressList?.data?.data?.rows]);

  const addressRowsForShipping = addressList?.data?.data?.rows || [];
  const selectedAddressRow = addressRowsForShipping.find(
    (r: any) => String(r?.id) === String(data?.address_id),
  );

  const deliveryInstructionsFromAddressRow = (row: any | undefined) => {
    if (!row) return "";
    return String(row.instructions ?? row.delivery_instructions ?? "").trim();
  };

  const selectedAddressInstructionsKey = selectedAddressRow
    ? `${String(selectedAddressRow.id)}:${deliveryInstructionsFromAddressRow(selectedAddressRow)}`
    : "";
  useEffect(() => {
    if (step !== 2) return;
    const instr = deliveryInstructionsFromAddressRow(selectedAddressRow);
    setShippingMeta((m) => ({ ...m, delivery_instructions: instr }));
  }, [step, data?.address_id, selectedAddressInstructionsKey]);

  /** If shipping method not set yet, adopt default from saved address row (e.g. after first inline save). */
  useEffect(() => {
    if (step !== 2) return;
    if (!data.address_id || data.shipping_id) return;
    const rows = addressList?.data?.data?.rows;
    const list = Array.isArray(rows) ? rows : [];
    const row = list.find((r: any) => String(r?.id) === String(data.address_id));
    const sid = row?.shipping_id;
    if (sid != null && String(sid).trim() !== "" && String(sid) !== "0") {
      setData((s) => ({ ...s, shipping_id: String(sid) }));
    }
  }, [step, data.address_id, data.shipping_id, addressList?.data?.data]);

  const selectedShippingRow = shippingList?.data?.data?.find(
    (r: any) => String(r?.id) === String(data?.shipping_id),
  );
  const selectedShippingLabel =
    selectedShippingRow?.shipping_details?.name ??
    selectedShippingRow?.name ??
    "—";

  const initialLoactionTypeForModal = useMemo(() => {
    const row = selectedAddressRow;
    if (!row) return undefined;
    const t = String(row.loaction_type ?? row.location_type ?? "").trim();
    if (t === "1" || t === "2" || t === "3") return t;
    return undefined;
  }, [selectedAddressRow]);

  /** Residence / billing from saved address (same as manual shipping review). */
  const residenceLabelFromAddressRow = (row: any): string | null => {
    if (!row) return null;
    const t = String(row.loaction_type ?? row.location_type ?? "").trim();
    if (t === "1") return LanguageConstants?.RESIDENTIAL ?? null;
    if (t === "2") return LanguageConstants?.LABORAL ?? null;
    if (t === "3") return LanguageConstants?.INDUSTRIAL ?? null;
    return null;
  };
  const addressBillingSameAsShipping = (row: any): boolean =>
    Number(row?.billing_same_as_shipping) === 1 ||
    row?.billing_same_as_shipping === true ||
    String(row?.billing_same_as_shipping) === "1";

  const reviewResidenceLabel = residenceLabelFromAddressRow(selectedAddressRow);
  const reviewBillingSame = addressBillingSameAsShipping(selectedAddressRow);

  const fileMetaLine = useMemo(() => {
    if (!fileName) return "";
    if (file && typeof file === "object" && "size" in file) {
      const kb = Math.max(1, Math.round((file as File).size / 1024));
      return `${LanguageConstants?.BULK_EXCEL_FILE_PREFIX} • ${kb}KB`;
    }
    return LanguageConstants?.BULK_EXCEL_FILE_PREFIX ?? "Excel File";
  }, [file, fileName]);

  const validateShipping = (): Record<string, string> | null => {
    const errobj: Record<string, string> = {};
    if (!data?.shipping_id) {
      errobj.shipping = LanguageConstants?.PLEASE_SELECT_SHIPPING_TYPE ?? "";
    }
    if (!data?.address_id) {
      errobj.address = LanguageConstants?.PLEASE_SELECR_DELIVER_ADDRESS ?? "";
    }
    return Object.keys(errobj).length ? errobj : null;
  };

  const validateSelectedBulkFile = async (f: File): Promise<boolean> => {
    try {
      const buf = await f.arrayBuffer();
      const v = validateBulkQuoteExcelBuffer(buf);
      if (v === "invalid_header") {
        toast.error(LanguageConstants?.BULK_EXCEL_INVALID_HEADER ?? "Please enter valid Excel sheet format.");
        return false;
      }
      if (v === "empty") {
        toast.error(
          getBulkExcelInvalidDataToastMessage(LanguageConstants?.BULK_EXCEL_ENTER_DATA),
        );
        return false;
      }
      return true;
    } catch {
      toast.error(
        getBulkExcelInvalidDataToastMessage(LanguageConstants?.BULK_EXCEL_ENTER_DATA),
      );
      return false;
    }
  };

  const goToStep = async (target: 1 | 2 | 3) => {
    if (target === step) return;
    if (target < step) {
      startTransition(() => setStep(target));
      return;
    }
    if (target === 2 && step === 1) {
      if (!file || typeof file !== "object" || !("arrayBuffer" in file)) {
        setError({ file: LanguageConstants?.SELECT_EXCEL ?? "" });
        return;
      }
      if (!(await validateSelectedBulkFile(file as File))) return;
      setError({});
      startTransition(() => setStep(2));
      return;
    }
    if (target === 3 && step === 2) {
      if (showInlineNewAddressForm) return;
      const rows = addressList?.data?.data?.rows || [];
      if (rows.length === 0) {
        firstAddressWizardRef.current?.submitWizard();
        return;
      }
      const e = validateShipping();
      if (e) {
        setError((prev) => ({ ...prev, ...e }));
        return;
      }
      setError((prev) => ({ ...prev, address: "", shipping: "" }));
      startTransition(() => setStep(3));
    }
  };

  const handelNavigateHome = () => {
    clearBulkWizardSession();
    navigate("/", { state: { data: { modelShow: true } } });
  };

  const selectFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const raw = input?.files?.[0];
    if (!raw) return;
    const ext = raw.name.split(".").pop()?.toLowerCase();
    if (ext !== "xlsx") {
      e.preventDefault();
      toast.error("Please upload a valid .xlsx file");
      input.value = "";
      return;
    }
    if (!(await validateSelectedBulkFile(raw))) {
      input.value = "";
      return;
    }
    setFileName(raw.name);
    setFile(raw);
    setExcelUrl("");
    setError((prev) => ({ ...prev, file: "" }));
  };

  const markTemplateDownloaded = () => {
    setTemplateDownloaded(true);
  };

  /** SS4 preview: upload temp copy → public URL → Office Online embed (sheet tabs). */
  const openBulkPreviewModal = async () => {
    if (!file || typeof file !== "object" || !("arrayBuffer" in file)) {
      toast.error(LanguageConstants?.SELECT_EXCEL ?? "Please upload a file first");
      return;
    }
    setPreviewError(null);
    setShowPreview(true);

    if (excelUrl) {
      setPreviewApiLoading(false);
      return;
    }

    setPreviewApiLoading(true);
    try {
      const body = (await xlsxPreview.makeRequest({
        payload: { file_path: file },
        type: ApiTypes.POSTAPIFORMDATA,
        url: ApiConstants?.QuotesXlxsPreview,
        toastStatus: false,
      })) as any;
      const url = xlsxPreviewUrlFromResponse(body);
      if (!url) {
        setPreviewError(VALIDATION_MESSAGE?.CATCH_ERROR ?? "Preview failed.");
        return;
      }
      setExcelUrl(url);
    } catch {
      setPreviewError(VALIDATION_MESSAGE?.CATCH_ERROR ?? "Preview failed.");
    } finally {
      setPreviewApiLoading(false);
    }
  };

  const handelXlsxClose = () => {
    setShowPreview(false);
    setPreviewApiLoading(false);
    setPreviewError(null);
  };

  const clearUploadedFile = () => {
    setFile("");
    setFileName("");
    setExcelUrl("");
    setShowPreview(false);
    setPreviewApiLoading(false);
    setPreviewError(null);
    setError((prev) => ({ ...prev, file: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearBulkWizardSession = () => {
    try {
      window.sessionStorage.removeItem(BULK_WIZARD_STATE_KEY);
    } catch {
      /* ignore */
    }
    setTemplateDownloaded(false);
    setStep(1);
    setData({});
    setShippingMeta({ delivery_instructions: "" });
    setFile("");
    setFileName("");
    setExcelUrl("");
    setShowPreview(false);
    setPreviewApiLoading(false);
    setPreviewError(null);
    setError({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteUploadedFile = () => {
    void Swal.fire({
      title: LanguageConstants?.BULK_REMOVE_UPLOAD_TITLE,
      html: `<p class="bulk-remove-upload-popup__line">${LanguageConstants?.BULK_REMOVE_UPLOAD_LINE1 ?? ""}</p><p class="bulk-remove-upload-popup__line">${LanguageConstants?.BULK_REMOVE_UPLOAD_LINE2 ?? ""}</p>`,
      showCancelButton: true,
      buttonsStyling: false,
      focusCancel: true,
      confirmButtonText: LanguageConstants?.BULK_REMOVE_UPLOAD_REMOVE,
      cancelButtonText: LanguageConstants?.BULK_REMOVE_UPLOAD_KEEP,
      customClass: {
        popup: "bulk-remove-upload-popup",
        title: "bulk-remove-upload-popup__title",
        htmlContainer: "bulk-remove-upload-popup__html",
        actions: "bulk-remove-upload-popup__actions",
        confirmButton: "bulk-remove-upload-popup__btn bulk-remove-upload-popup__btn--remove",
        cancelButton: "bulk-remove-upload-popup__btn bulk-remove-upload-popup__btn--keep",
      },
    }).then((r) => {
      if (!r.isConfirmed) return;
      clearUploadedFile();
    });
  };

  const handleSubmitQuote = async () => {
    if (Add?.loading) return;
    const shipErr = validateShipping();
    if (!file || typeof file !== "object" || !("arrayBuffer" in file)) {
      setError({ ...(shipErr ?? {}), file: LanguageConstants?.SELECT_EXCEL ?? "" });
      return;
    }
    if (!(await validateSelectedBulkFile(file as File))) return;
    if (shipErr) {
      setError((prev) => ({ ...prev, ...shipErr }));
      startTransition(() => setStep(2));
      return;
    }
    setError((prev) => ({ ...prev, address: "", shipping: "" }));
    const payload = {
      shipping_id: data?.shipping_id,
      address_id: data?.address_id,
      excel_file: file,
    };
    try {
      const apiBody = (await Add.makeRequest({
        payload,
        type: ApiTypes.POSTAPIFORMDATA,
        url: ApiConstants?.QuotesAddExcel,
        toastStatus: false,
      })) as any;
      if (!apiBody || apiBody.status != true) {
        const errMsg = apiBody?.message ?? VALIDATION_MESSAGE?.CATCH_ERROR;
        toast.error(typeof errMsg === "string" ? errMsg : VALIDATION_MESSAGE?.CATCH_ERROR);
        return;
      }

      const payloadSocket = { sender_type: "1", receiver_id: "1" };
      socket.emit("admin_count", payloadSocket, async () => {});

      const incorr = apiBody?.data?.inCorrectData;
      const hasIncorrect = Array.isArray(incorr) && incorr.length >= 1;
      if (hasIncorrect) {
        navigate("/incorrect-excel", { state: { data: incorr } });
        return;
      }

      const rawQuotationId =
        apiBody?.data?.data?.quotation_id ?? apiBody?.data?.quotation_id ?? apiBody?.data?.quotationId;
      const formattedPrimary = formatQuotationIdForSuccessPopup(rawQuotationId);
      const dynamicQuoteId =
        formattedPrimary !== "—"
          ? formattedPrimary
          : (() => {
              const fromPaths = readQuotationIdFromQuotesCreateSuccess(apiBody);
              return fromPaths !== "—" ? fromPaths : extractQuotationIdFromApiBody(apiBody);
            })();

      void Swal.fire({
        html: `
          <div class="manual-quote-success-modal">
            <img src="${QUOTE_SUCCESS_LOGO_URL}" alt="Quote N Buy" class="manual-quote-success-modal__logo" width="128" height="128" decoding="async" />
            <h2 class="manual-quote-success-modal__title">${LanguageConstants?.QUOTE_SUCCESS_THANK_YOU || ""}</h2>
            <p class="manual-quote-success-modal__id">Quote ID: ${dynamicQuoteId}</p>
            <p class="manual-quote-success-modal__sub">${LanguageConstants?.QUOTE_SUCCESS_RECEIVED || ""}</p>
            <p class="manual-quote-success-modal__desc">${LanguageConstants?.QUOTE_SUCCESS_REVIEWING || ""}</p>
          </div>
        `,
        customClass: {
          popup: "manual-quote-success-popup",
          confirmButton: "manual-quote-success-popup__home-btn",
        },
        buttonsStyling: false,
        confirmButtonText: LanguageConstants?.HOME || "Home",
        showConfirmButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        timer: 5000,
        timerProgressBar: true,
      }).then(() => {
        clearBulkWizardSession();
        navigate("/");
      });
    } catch {
      /* makeRequest already toasts when toastStatus !== false; bulk uses false */
      toast.error(VALIDATION_MESSAGE?.CATCH_ERROR ?? "");
    }
  };

  const showBulkSubmitLoader = Boolean(Add?.loading);

  return (
    <>
      <main>
        <div className="head-space footer-space">
          <div className="inner-content">
            <div className="container">
              <div className="manual-quote-page">
                <div className="manual-quote-wizard">
                  <div className="manual-quote-wizard__shell">
                    <BrandedLoader show={showBulkSubmitLoader} scope="local" />
                    <div className="manual-quote-wizard__top manual-quote-wizard__top--with-title">
                      <div className="manual-quote-wizard__top-start">
                        {step === 1 ? (
                          <button type="button" className="manual-quote-wizard__back" onClick={handelNavigateHome}>
                            <i className="icon-chev-right" /> {LanguageConstants?.BULK_ENTRY_NAV}
                          </button>
                        ) : (
                          <h2 className="manual-quote-wizard__page-title">{LanguageConstants?.BULK_ENTRY_NAV}</h2>
                        )}
                      </div>
                      <div className="manual-quote-wizard__stepper-wrap">
                        <div className="rq-stepper-static" aria-label="Bulk entry steps">
                          <button
                            type="button"
                            className={`rq-stepper-static__item rq-stepper-static__trigger${step > 1 ? " rq-stepper-static__item--complete" : ""}${step === 1 ? " rq-stepper-static__item--active" : ""}`}
                            onClick={() => goToStep(1)}
                          >
                            <span className="rq-stepper-static__line" aria-hidden />
                            <span className="rq-stepper-static__dot" aria-hidden>
                              {step >= 1 ? <i className="icon-tick-2" /> : null}
                            </span>
                            <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_DATA_ENTRY}</span>
                          </button>
                          <button
                            type="button"
                            className={`rq-stepper-static__item rq-stepper-static__trigger${step > 2 ? " rq-stepper-static__item--complete" : ""}${step === 2 ? " rq-stepper-static__item--active" : ""}`}
                            onClick={() => goToStep(2)}
                          >
                            <span className="rq-stepper-static__line" aria-hidden />
                            <span className="rq-stepper-static__dot" aria-hidden>
                              {step >= 2 ? <i className="icon-tick-2" /> : null}
                            </span>
                            <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_SHIPPING}</span>
                          </button>
                          <button
                            type="button"
                            className={`rq-stepper-static__item rq-stepper-static__trigger${step === 3 ? " rq-stepper-static__item--active rq-stepper-static__item--complete" : ""}`}
                            onClick={() => {
                              if (step === 2) goToStep(3);
                            }}
                          >
                            <span className="rq-stepper-static__line" aria-hidden />
                            <span className="rq-stepper-static__dot" aria-hidden>
                              {step >= 3 ? <i className="icon-tick-2" /> : null}
                            </span>
                            <span className="rq-stepper-static__label">{LanguageConstants?.WIZARD_STEP_REVIEW}</span>
                          </button>
                        </div>
                      </div>
                      <div className="manual-quote-wizard__top-spacer" aria-hidden />
                    </div>

                    {step === 1 && (
                      <div className="inner-height d-flex flex-column">
                        <p className="bulk-entry-intro">{LanguageConstants?.BULK_ENTRY_TAGLINE}</p>
                        <div className="row my-auto">
                          <div className="col-xl-9 mx-auto">
                            <div className="bulk-entry-actions">
                              {!templateDownloaded ? (
                                <a
                                  href={TEMPLATE_HREF}
                                  download
                                  className="excel-btn"
                                  onClick={markTemplateDownloaded}
                                >
                                  <div className="excel-btn__copy">
                                    <img className="excel-img" src={excelImg2} alt="" />
                                    <div style={{ minWidth: 0 }}>
                                      <p className="excel-txt mb-0">{LanguageConstants?.BULK_DOWNLOAD_CARD_TITLE}</p>
                                      <p className="excel-txt-sub mb-0">{LanguageConstants?.BULK_DOWNLOAD_CARD_SUB}</p>
                                    </div>
                                  </div>
                                  <span className="excel-icon" aria-hidden>
                                    <i className="icon-download-2" />
                                  </span>
                                </a>
                              ) : (
                                <a
                                  href={TEMPLATE_HREF}
                                  download
                                  className="excel-btn excel-btn--template-ss2"
                                  onClick={markTemplateDownloaded}
                                >
                                  <div className="excel-btn__copy">
                                    <img className="excel-img" src={excelImg2} alt="" />
                                    <div style={{ minWidth: 0 }}>
                                      <p className="excel-txt mb-0">{LanguageConstants?.BULK_REDOWNLOAD_CARD_TITLE}</p>
                                      <p className="excel-txt-sub mb-0">{LanguageConstants?.BULK_DOWNLOAD_CARD_SUB}</p>
                                    </div>
                                  </div>
                                  <span className="excel-icon" aria-hidden>
                                    <i className="icon-download-2" />
                                  </span>
                                </a>
                              )}

                              <div className="bulk-entry-upload-slot">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  className="d-none"
                                  id="uploadExcelFileBulk"
                                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                  onChange={selectFileHandler}
                                />
                                {!file ? (
                                  <label htmlFor="uploadExcelFileBulk" className="excel-btn excel-btn-blue cursor-pointer">
                                    <div className="excel-btn__copy">
                                      <img className="excel-img" src={excelImg2} alt="" />
                                      <div style={{ minWidth: 0 }}>
                                        <p className="excel-txt mb-0">{LanguageConstants?.BULK_UPLOAD_CARD_TITLE}</p>
                                        <p className="excel-txt-sub mb-0">{LanguageConstants?.BULK_UPLOAD_CARD_SUB}</p>
                                      </div>
                                    </div>
                                    <span className="excel-icon" aria-hidden>
                                      <i className="icon-upload-3" />
                                    </span>
                                  </label>
                                ) : (
                                  <label
                                    htmlFor="uploadExcelFileBulk"
                                    className="excel-btn excel-btn-blue excel-btn--uploaded-ss2 cursor-pointer"
                                    aria-label={LanguageConstants?.BULK_UPLOAD_REPLACE_FILE}
                                  >
                                    <div className="excel-btn__copy">
                                      <img className="excel-img" src={excelImg2} alt="" />
                                      <div style={{ minWidth: 0 }}>
                                        <p className="excel-txt mb-0">{LanguageConstants?.BULK_UPLOAD_UPLOADED_CARD_TITLE}</p>
                                        <p className="excel-txt-sub excel-txt-sub--filename mb-0">{fileName}</p>
                                      </div>
                                    </div>
                                    <span className="excel-icon excel-icon--uploaded-success" aria-hidden>
                                      <i className="icon-tick-2" />
                                    </span>
                                  </label>
                                )}
                                {error?.file ? <p className="bulk-entry-upload-error mb-0">{error.file}</p> : null}
                              </div>
                            </div>
                            {file ? (
                              <div className="bulk-upload-ss2-actions">
                                <button
                                  type="button"
                                  className="bulk-upload-ss2-actions__btn bulk-upload-ss2-actions__btn--delete"
                                  onClick={handleDeleteUploadedFile}
                                >
                                  {LanguageConstants?.BULK_DELETE_UPLOADED_FILE}
                                </button>
                                <button
                                  type="button"
                                  className="bulk-upload-ss2-actions__btn bulk-upload-ss2-actions__btn--preview"
                                  onClick={() => openBulkPreviewModal()}
                                >
                                  {LanguageConstants?.BULK_PREVIEW_FILE_SHORT}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="manual-quote-footer manual-quote-footer--step mt-auto">
                          <span />
                          <div className="manual-quote-footer__end">
                            <button
                              type="button"
                              className="manual-quote-btn manual-quote-btn--primary com-btn rounded-pill"
                              onClick={() => goToStep(2)}
                            >
                              {LanguageConstants?.NEXT}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="manual-quote-shipping manual-quote-shipping--wizard mt-2">
                        <div className="row">
                          <div className="col-12 col-xl-10 mx-auto">
                            {addressList.loading ? (
                              <p className="text-muted text-center small py-4 mb-0">Loading addresses…</p>
                            ) : addressRowsForShipping.length === 0 ? (
                              <>
                                <div className="manual-quote-inline-address px-sm-4">
                                  <NewAddress
                                    ref={firstAddressWizardRef}
                                    key={firstAddressFormKey}
                                    wizardInline
                                    handleClose={() => {}}
                                    initialLoactionType={initialLoactionTypeForModal}
                                    onAddressCreated={(newId: string | number, created?: any) => {
                                      const idStr = String(newId);
                                      const sid = created?.shipping_id;
                                      const instr = String(
                                        created?.instructions ?? created?.delivery_instructions ?? "",
                                      ).trim();
                                      setData((s) => ({
                                        ...s,
                                        address_id: idStr,
                                        ...(sid != null && String(sid).trim() !== "" && String(sid) !== "0"
                                          ? { shipping_id: String(sid) }
                                          : {}),
                                      }));
                                      setShippingMeta((m) => ({
                                        ...m,
                                        delivery_instructions: instr || m.delivery_instructions,
                                      }));
                                      addressList.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
                                      setFirstAddressFormKey((k) => k + 1);
                                    }}
                                  />
                                </div>
                                {error.address || error.shipping ? (
                                  <div className="px-sm-4 mt-2">
                                    {error.address ? (
                                      <p className="text-danger small mb-1">{error.address}</p>
                                    ) : null}
                                    {error.shipping ? (
                                      <p className="text-danger small mb-0">{error.shipping}</p>
                                    ) : null}
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <>
                                <div className="manual-quote-shipping__title-row">
                                  <h3 className="manual-quote-shipping__section-title mb-0">
                                    {LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION}
                                  </h3>
                                  


                                </div>
                                <div className="manual-quote-shipping__radios">
                                  {addressRowsForShipping.map((addr: any) => (
                                    <div key={addr.id} className="form-check">
                                      <input
                                        type="radio"
                                        id={`bulk-ship-address-${addr.id}`}
                                        name="bulk_ship_address"
                                        className="form-check-input"
                                        value={addr.id}
                                        checked={String(data.address_id) === String(addr.id)}
                                        onChange={() => {
                                          const idStr = String(addr.id);
                                          setShowInlineNewAddressForm(false);
                                          setData((d) => ({ ...d, address_id: idStr, shipping_id: "" }));
                                          setShippingMeta((m) => ({
                                            ...m,
                                            delivery_instructions: deliveryInstructionsFromAddressRow(addr),
                                          }));
                                          setError((e) => ({ ...e, address: "" }));
                                        }}
                                      />
                                      <label className="form-check-label" htmlFor={`bulk-ship-address-${addr.id}`}>
                                        {formatAddressListRow(addr)}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                                {error.address ? (
                                  <div className="px-sm-4 mb-2">
                                    <p className="text-danger small mb-0">{error.address}</p>
                                  </div>
                                ) : null}

{!showInlineNewAddressForm ? (
                                    <div className="manual-quote-add-address">
                                      <button
                                        type="button"
                                        className="manual-quote-add-btn com-btn rounded-pill px-3"
                                        onClick={() => {
                                          setAddressModalKey((k) => k + 1);
                                          setShowInlineNewAddressForm(true);
                                        }}
                                      >
                                       {LanguageConstants?.NEWADDRES}
                                      </button>
                                    </div>
                                  ) : null}

                                {showInlineNewAddressForm ? (
                                  <div className="manual-quote-inline-address manual-quote-inline-address--between px-sm-4">
                                    <NewAddress
                                      key={`inline-new-addr-${addressModalKey}`}
                                      wizardInline
                                      showSubmitInForm
                                      showCancelInForm
                                      hideShipMethodAndPrimaryInForm
                                      submitLabel={LanguageConstants?.CONFIRMA ?? "Confirm"}
                                      handleClose={() => setShowInlineNewAddressForm(false)}
                                      initialLoactionType={initialLoactionTypeForModal}
                                      onAddressCreated={(newId: string | number, created?: any) => {
                                        const idStr = String(newId);
                                        const sid = created?.shipping_id;
                                        const instr = String(
                                          created?.instructions ?? created?.delivery_instructions ?? "",
                                        ).trim();
                                        setData((s) => ({
                                          ...s,
                                          address_id: idStr,
                                          ...(sid != null && String(sid).trim() !== "" && String(sid) !== "0"
                                            ? { shipping_id: String(sid) }
                                            : {}),
                                        }));
                                        setShippingMeta((m) => ({
                                          ...m,
                                          delivery_instructions: instr || m.delivery_instructions,
                                        }));
                                        setShowInlineNewAddressForm(false);
                                        addressList.makeRequest({ url: ApiConstants?.AddressList, toastStatus: false });
                                      }}
                                    />
                                  </div>
                                ) : null}

                                <div className="manual-quote-shipping__divider" />

                                <h3 className="manual-quote-shipping__section-title">
                                  {LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION}
                                </h3>
                                <div>
                                  <p className="manual-quote-shipping__label">{LanguageConstants?.SHIPPINGMETHOD}</p>
                                  <div className="manual-quote-shipping__radio-row mb-3">
                                    {(shippingList?.data?.data || []).map((m: any) => (
                                      <div key={m.id} className="form-check">
                                        <input
                                          type="radio"
                                          id={`bulk-ship-method-${m.id}`}
                                          name="bulk_shipping_method"
                                          className="form-check-input"
                                          value={m.id}
                                          checked={String(data.shipping_id) === String(m.id)}
                                          onChange={() => {
                                            setData((d) => ({ ...d, shipping_id: String(m.id) }));
                                            setError((e) => ({ ...e, shipping: "" }));
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor={`bulk-ship-method-${m.id}`}>
                                          {m?.shipping_details?.name}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                  {error.shipping ? (
                                    <p className="text-danger small mb-2">{error.shipping}</p>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="manual-quote-footer manual-quote-footer--step">
                          <button
                            type="button"
                            className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill"
                            onClick={() => goToStep(1)}
                          >
                            {LanguageConstants?.BACK}
                          </button>
                          <span
                            className={showInlineNewAddressForm ? "d-inline-block" : undefined}
                            title={
                              showInlineNewAddressForm
                                ? getFromStorage({ key: StorageConstant?.Translate }) === "spa"
                                  ? "Cierre la sección de nueva dirección para continuar"
                                  : "Close the new address section to continue"
                                : undefined
                            }
                          >
                            <button
                              type="button"
                              className="manual-quote-btn com-btn rounded-pill"
                              onClick={() => goToStep(3)}
                              disabled={showInlineNewAddressForm}
                              style={showInlineNewAddressForm ? { pointerEvents: "none" } : undefined}
                            >
                              {LanguageConstants?.REVIEW}
                            </button>
                          </span>
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <>
                        <div className="bulk-entry-review__layout inner-height">
                          <div className="bulk-entry-review__preview">
                            <div className="d-flex align-items-center justify-content-center mb-3 gap-2">
                              <img className="bulk-entry-review__preview-excel" src={excelImg2} alt="" />
                              <div>
                                <p className="bulk-entry-review__preview-name">{fileName || "—"}</p>
                                <p className="bulk-entry-review__preview-meta">{fileMetaLine}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="manual-quote-btn manual-quote-btn--primary com-btn rounded-pill mx-auto preview-excel-btn"
                              onClick={() => openBulkPreviewModal()}
                            >
                              {LanguageConstants?.BULK_PREVIEW_UPLOADED_FILE}
                            </button>
                          </div>
                          <aside className="bulk-entry-review__aside">
                            <div className="manual-quote-ship-card">
                              <div className="manual-quote-ship-card__head">
                                <img src={truckIcon} alt="" className="manual-quote-ship-card__icon" />
                                {LanguageConstants?.MANUAL_QUOTE_SHIPPING_DETAILS}
                              </div>
                              <div className="manual-quote-ship-card__body">
                                <p>
                                  <strong>{LanguageConstants?.DELADDRESS}</strong>
                                  {selectedAddressRow
                                    ? formatAddressListRow(selectedAddressRow)
                                    : "—"}
                                </p>
                                {shippingMeta.delivery_instructions ? (
                                  <p>
                                    <strong>{LanguageConstants?.MANUAL_QUOTE_INSTRUCTIONS}</strong>
                                    {shippingMeta.delivery_instructions}
                                  </p>
                                ) : null}
                                {reviewResidenceLabel ? (
                                  <p>
                                    <strong>{LanguageConstants?.MANUAL_QUOTE_RESIDENCE}</strong>
                                    {reviewResidenceLabel}
                                  </p>
                                ) : null}
                                <p>
                                  <strong>{LanguageConstants?.SHIPINNG}</strong>
                                  {selectedShippingLabel}
                                </p>
                                {reviewBillingSame ? (
                                  <p className="mb-0 text-muted small">{LanguageConstants?.MANUAL_QUOTE_BILLING_SAME}</p>
                                ) : null}
                              </div>
                            </div>
                          </aside>
                        </div>

                        <div className="manual-quote-footer manual-quote-footer--step">
                          <button
                            type="button"
                            className="manual-quote-btn manual-quote-btn--muted com-btn rounded-pill"
                            onClick={() => goToStep(2)}
                          >
                            {LanguageConstants?.BACK}
                          </button>
                          <button
                            type="button"
                            className="manual-quote-btn com-btn rounded-pill"
                            onClick={handleSubmitQuote}
                            disabled={showBulkSubmitLoader}
                            aria-busy={showBulkSubmitLoader}
                          >
                            {LanguageConstants?.REQUEST_A_QUOTE}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        show={showPreview}
        onHide={handelXlsxClose}
        centered
        className="com-modal text-center excel-modal"
        dialogClassName="modal-dialog-centered modal-xl w-auto"
        contentClassName="excel-preview-modal__content"
      >
        <ExcelSheet
          key={excelUrl || "preview"}
          data={excelUrl}
          fileName={fileName}
          apiLoading={previewApiLoading}
          error={previewError}
          onClose={handelXlsxClose}
        />
      </Modal>
    </>
  );
}

export default ExcelSelect;
